import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import { DataStorageService } from '../../services/data-storage.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import usStates from '../../shared/data/us-states.json';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';

interface PersonalDetails {
  streetAddress: string;
  suiteUnitOffice?: string;
  city: string;
  state: string;
  zipCode: string;
}

interface BusinessLocationPreferences {
  sameAsPersonalAddress: boolean;
}

@Component({
    selector: 'app-business-location',
    templateUrl: './business-location.component.html',
    styleUrl: './business-location.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessLocationComponent extends AcceptInvitationStepBase<any> {
  
  // Separate key to persist preferences for this component
  private readonly preferencesKey = 'businessLocationPreferences';

  public readonly commService: InvitationCommunicationService = inject(InvitationCommunicationService);
  
  // Component property that will be persisted with a different key
  public sameAsPersonalAddress: boolean = false;
  
  public override readonly form = inject(FormBuilder).group({
    streetAddress: ['', Validators.required],
    suiteUnitOffice: [''],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]]
  });

  public override get nextUrl() {
    // Determine next URL based on user role
    if (this.commService.role === 'supplier') {
      // If the user is a supplier, go to link-bank-account
      return '../link-bank-account';
    } else if (this.commService.role === 'borrower') {
      // If the user is a borrower, go to ownership-disclosure
      return '../ownership-disclosure';
    } else {
      // Invalid role, default to link-bank-account
      console.error("Invalid user role:", this.commService.role);
      return '../link-bank-account';
    }
  }

  public override readonly previousUrl = '../business-structure';
  protected override readonly storageKey = 'businessLocationForm';

  public readonly usStates = usStates;

  public onSameAddressChange(event: MatCheckboxChange): void {
    if (event.checked) {
      // If checked, load personal address and disable fields
      this.loadPersonalData();
    } else {
      // Enable fields only when the user unchecks the checkbox
      Object.keys(this.form.controls).forEach(key => {
        if (key !== 'sameAsPersonalAddress') {
          this.form.get(key)?.enable();
          this.form.get(key)?.setValue('');
        }
      });
    }
  }

  /**
   * Saves the component preferences using a different key
   * from the form data
   * 
   * @deprecated If personal address is same as business location, just rewrite the fields and save as normal
   */
  private persistPreferences(): void {
    const preferences: BusinessLocationPreferences = {
      sameAsPersonalAddress: this.sameAsPersonalAddress
    };
    this.dataStorageService.setItem(this.preferencesKey, preferences);
  }

  /**
   * Loads the component preferences
   */
  private loadPreferences(): void {
    const preferences = this.dataStorageService.getItem<BusinessLocationPreferences>(this.preferencesKey);
    if (preferences) {
      this.sameAsPersonalAddress = preferences.sameAsPersonalAddress;
    }
  }

  public override loadFormData(): void {
    super.loadFormData();
    
    // Load saved preferences
    this.loadPreferences();
    
    // If the checkbox was checked, load personal data
    if (this.form.get('sameAsPersonalAddress')?.value) {
      this.loadPersonalData();
    }
  }

  loadPersonalData() {
    const personalDetails = this.dataStorageService.getItem<PersonalDetails>('personalDetailsForm');
    if (personalDetails) {
      // Disable fields and set values
      const fieldsToUpdate: (keyof PersonalDetails)[] = ['streetAddress', 'suiteUnitOffice', 'city', 'state', 'zipCode'];
      fieldsToUpdate.forEach(key => {
        const value = personalDetails[key];
        if (key in personalDetails && value !== undefined) {
          this.form.get(key)?.setValue(value);
          this.form.get(key)?.disable();
        }
      });
    }
  }

  public onlyNumberKey(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /**
   * Overrides the continue method to persist preferences
   * before calling the base class continue method
   * 
   * @deprecated If personal address is same as business location, just rewrite the fields and save as normal
   */
  public override async continue(): Promise<void> {
    if (this.form.valid || this.form.disabled) { // Check for disabled to catch same address case
      this.persistFormData();
      if (this.nextUrl) {
        await this.router.navigate([this.nextUrl], { relativeTo: this.route });
      }
    } else {
      this.markAllFieldsAsTouchedIfInvalid();
    }
  }
  /* Old case (discontinued)
  public override async continue(): Promise<void> {
    this.persistPreferences();
    await super.continue();
  }
  */
}